import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import type { ResolvedReleaseStory, StoryAccent } from 'fym-shared/src/releases/types';
import { keyedStoryRuns, stripStoryMarkup } from 'fym-shared/src/releases/storyMarkup';
import { TypedMemo } from '../../common/types';
import { Panel } from '../components/primitives/Panel';
import { whatsNewMessages as m } from './whatsNewMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
import { WhatsNewStoryImage } from './WhatsNewStoryImage';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the panel; each part uses its own slice of it */
import styles from './WhatsNew.module.scss';
/* eslint-enable css-modules/no-unused-class */

const ACCENT_CLASS: Record<StoryAccent, string | undefined> = {
  yellow: styles.accYellow,
  pink: styles.accPink,
  purple: styles.accPurple,
  green: styles.accGreen,
  blue: styles.accBlue,
};

interface Props {
  story: ResolvedReleaseStory;
  /** The position among the story cards of this panel, from 1. */
  index: number;
}

/**
 * One story card (design whats-new a1): the promo image when there is one, the numbered tag in the story accent, the headline, the
 * lede with `**bold**` runs, Before / Now only when both exist, then the note. The runs are text
 * nodes, so release copy cannot inject HTML.
 */
const WhatsNewStoryCardComponent: React.FC<Props> = ({ story, index }) => {
  const intl = useIntl();
  const runs = useMemo(() => keyedStoryRuns(story.lede), [story.lede]);
  const hasPair = story.before !== undefined && story.before !== '' && story.now !== undefined && story.now !== '';
  const hasNote = story.note !== undefined && story.note !== '';

  return (
    <Panel as="li" className={clsx(styles.story, ACCENT_CLASS[story.accent])}>
      {story.image !== undefined && <WhatsNewStoryImage image={story.image} alt={stripStoryMarkup(story.headline)} />}
      <span className={styles.tag}>
        <b className={styles.tagNumber}>{String(index).padStart(2, '0')}</b>
        {story.tag}
      </span>
      <h3 className={styles.headline}>{withBrandWord(story.headline)}</h3>
      <p className={styles.lede}>
        {runs.map((run) =>
          run.bold ? (
            <b key={run.key}>{withBrandWord(run.text)}</b>
          ) : (
            <React.Fragment key={run.key}>{withBrandWord(run.text)}</React.Fragment>
          )
        )}
      </p>
      {hasPair && (
        <div className={styles.pair}>
          <div className={styles.before}>
            <b className={styles.pairLabel}>{intl.formatMessage(m.before)}</b>
            {story.before}
          </div>
          <div className={styles.now}>
            <b className={styles.pairLabel}>{intl.formatMessage(m.now)}</b>
            {story.now}
          </div>
        </div>
      )}
      {hasNote && <p className={styles.note}>{story.note}</p>}
    </Panel>
  );
};

export const WhatsNewStoryCard = TypedMemo(WhatsNewStoryCardComponent);
